import { Component, ViewChild, AfterViewInit, signal, computed, OnDestroy } from '@angular/core';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { Router } from "@angular/router";
import { ProductionBatchState } from "../../../store/batch/batch.state";
import { Color, Lab, PoState, poStateTranslation, ProductionBatch } from "../../../model";
import { NzTableComponent, NzTableModule } from "ng-zorro-antd/table";
import { animate, style, transition, trigger } from "@angular/animations";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { distinctUntilChanged, tap } from "rxjs";
import { map } from "rxjs/operators";
import data from 'src/assets/baseMap.json';
import { toUpper } from "lodash";
import { AuthenticationState } from "../../../store/auth/auth.state";
import { UserRole } from "src/model";
import { UserPreferencesService } from "src/services/user-preferences.service";
import { CommonModule, DatePipe, DecimalPipe, I18nSelectPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { LuxonModule } from 'luxon-angular';
import { PipesModule } from '../../pipes/pipes.module';
import { ComponentsModule } from '../components.module';
import { JsonObjectPreviewComponent } from '../json-object-preview/json-object-preview.component';

@UntilDestroy()
@Component({
	selector: 'app-work-batches',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		RouterLink,
		NzTableModule,
		NzGridModule,
		NzInputModule,
		NzRadioModule,
		NzAutocompleteModule,
		NzButtonModule,
		NzIconModule,
		NzTooltipModule,
		NzPopconfirmModule,
		ClipboardModule,
		LuxonModule,
		I18nSelectPipe,
		DecimalPipe,
		DatePipe,
		PipesModule,
		ComponentsModule,
		JsonObjectPreviewComponent,
	],
	templateUrl: './work-batches.component.html',
	animations: [
		trigger('highlight', [
			transition(':increment', [
				style({ backgroundColor: 'lightgreen' }),
				animate('3000ms', style({ backgroundColor: 'undefined' })),
			])
		])
	]
})
export class WorkBatchesComponent implements AfterViewInit, OnDestroy {
	@ViewChild(NzTableComponent, { static: true }) table!: NzTableComponent<ProductionBatch>;

	private static readonly PREF_KEY_CURRENT_ONLY = 'work-batches.batch-list.show-current-only-filter';

	data: any = data;
	poNamePrefix?: string = undefined;
	currentOnly: boolean = true;

	PoState = PoState;
	poStateTranslation = poStateTranslation;
	virtualItemSize: number = 55;
	pageSize = 50;

	readonly isAdmin = computed(() => this.authState.hasRole(UserRole.Admin));

	/** Id of the batch whose PO name was just copied — drives the check-mark feedback on the copy button. */
	readonly copiedBatchId = signal<string | null>(null);
	private copiedResetTimer?: ReturnType<typeof setTimeout>;

	readonly previewBatch = signal<ProductionBatch | null>(null);
	readonly previewBatchTitle = computed(() => {
		const b = this.previewBatch();
		return b ? `Batch ${b.poName} / ${b.sequenceInPo + 1}` : null;
	});

	constructor(
		public state: ProductionBatchState,
		public authState: AuthenticationState,
		private router: Router,
		private prefs: UserPreferencesService,
	) {
		if (!authState.hasExactlyRole(UserRole.Operator)) {
			this.currentOnly = prefs.get(WorkBatchesComponent.PREF_KEY_CURRENT_ONLY, true);
		}
	}

	ngAfterViewInit(): void {
		this.table?.cdkVirtualScrollViewport?.scrolledIndexChange.pipe(
			untilDestroyed(this),
			map(n => Math.ceil(n / this.pageSize) + 1),
			distinctUntilChanged(),
			tap(n => console.log("Loading page", n)),
			tap(p => this.state.loadPage(p, this.pageSize, this.poNamePrefix, this.currentOnly))
		).subscribe();
	}

	get poLabels(): string[] {
		return this.state.allLoaded.map(b => b.poName);
	}

	onPoNameCopied(batchId: string) {
		this.copiedBatchId.set(batchId);
		clearTimeout(this.copiedResetTimer);
		this.copiedResetTimer = setTimeout(() => this.copiedBatchId.set(null), 1500);
	}

	ngOnDestroy(): void {
		clearTimeout(this.copiedResetTimer);
	}

	trackByIndex(_: number, data: ProductionBatch): string {
		return data.id;
	}

	onSearchInput(event: Event) {
		this.onSearch((event.target as HTMLInputElement).value);
	}

	onSearch(prefix: string | undefined) {
		this.poNamePrefix = prefix;
		this.state.getPage(undefined, undefined, this.poNamePrefix);
	}

	onSearchEnter(event: Event) {
		event.preventDefault();
		const matchingBatch = this.state.allLoaded.find(b =>
			this.poNamePrefix ? b.poName.toLowerCase().startsWith(this.poNamePrefix.toLowerCase()) : true
		);
		if (matchingBatch) {
			this.router.navigate(['/pos', matchingBatch.id]);
		}
	}

	onSliceChange(currentOnly: boolean) {
		if (!this.authState.hasExactlyRole(UserRole.Operator)) {
			this.prefs.set(WorkBatchesComponent.PREF_KEY_CURRENT_ONLY, currentOnly);
		}
		this.state.getPage(undefined, undefined, this.poNamePrefix, currentOnly);
	}

	toColorTooltip(color: Color, mult: number): string {
		let res = mult * 3 + "kg - ";
		for (let i = 0; i < 26; i++) {
			let chr = String.fromCharCode(97 + i);
			if ((color as any)[chr]) {
				res += toUpper(chr) + ": " + ((color as any)[chr]) * mult + ", ";
			}
		}
		res = res.slice(0, -2);
		return res;
	}

	colorPicker(color: Lab): string {
		if (!color?.l) return "url(./assets/images/close.png)  center/contain";
		return `lab(${color?.l} ${color?.a} ${color?.b})`;
	}

	attach([subject, to]: [string, ProductionBatch]) {
		this.state.attach(subject, to.id);
	}

	truncate(batch: ProductionBatch, number: number) {
		this.state.truncate(batch.id, batch.producingQuantity + number);
	}

	deleteBatch(batch: ProductionBatch) {
		console.debug('[work-batches] deleteBatch: requesting delete of batch', batch.id, batch.poName,
			'(scanCount=' + (batch.scans?.length ?? 0) + ')');
		this.state.delete(batch.id).subscribe({
			next: () => console.debug('[work-batches] deleteBatch: server confirmed delete of batch', batch.id),
			error: err => console.error('[work-batches] deleteBatch: server returned error for batch', batch.id, err),
		});
	}

	cancel() { }
}
